import { KatFileUploadStatus } from '@amzn/katal-components/es/file-upload';
import { FileUploadState } from 'src/components/pages/campaignAutomation/input/assets';
import { StateConstants } from 'src/constants';
import { AssetState, AssetStatus } from 'src/types';

/**
 * Validates if a given string is a valid URL
 * @param urlString - The string to validate
 * @returns boolean indicating if the string is a valid http/https URL
 */
export const validateUrl = (urlString: string): boolean => {
  if (!urlString?.trim()) return false;
  try {
    const url = new URL(urlString.trim());
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
};

/**
 * Maps AssetStatus to KatFileUpload state
 * @param status - Current AssetStatus
 * @param error - Error message if any
 * @returns FileUploadState object for KatFileUpload
 */
export const getFileUploadState = (status?: AssetStatus, error?: string): FileUploadState => ({
  fileUploadState: {
    status: (() => {
      switch (status) {
        case AssetStatus.COMPLETED:
          return KatFileUploadStatus.COMPLETE;
        case AssetStatus.PENDING_UPLOAD:
          return KatFileUploadStatus.UPLOADING;
        case AssetStatus.FAILED:
        case AssetStatus.BASIC_VALIDATION_FAILED:
          return KatFileUploadStatus.ERROR;
        default:
          return KatFileUploadStatus.READY;
      }
    })(),
    error:
      status === AssetStatus.FAILED
        ? (error ?? StateConstants.EMPTY_STRING)
        : StateConstants.EMPTY_STRING,
  },
});

/**
 * Type guard to check if an asset is completed and has a valid key
 * @param asset Partial asset state or undefined
 * @returns Type predicate indicating if asset is completed with valid key
 */
export const isCompletedAsset = (
  asset: Partial<AssetState> | undefined,
): asset is AssetState & { key: string } => {
  return Boolean(asset?.status === AssetStatus.COMPLETED);
};
